import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';
import { ICON_BY_TYPE } from '~/modules/link-farm/components/LinkList/link-item-icons';

const BASE_LINK_ITEM_CLASSES = cn(
  'flex',
  'justify-between',
  'rounded-sm',
  'border',
  'bg-emphasis-default',
  'border-emphasis-subtle',
  'group',
  'transition',
  'duration-300',
  'hover:bg-default-inverse/subtle',
  'hover:border-primary-default',
  'focus:border-opacity-stronger',
  'focus:bg-default-inverse/subtle',
  'focus:border-white',
);

interface ReportLink {
  titleKey: string;
  descriptionKey: string;
  path: string;
}

const REPORT_LINKS: ReportLink[] = [
  {
    titleKey: 'reports.links.globalStreamsByDsp.title',
    descriptionKey: 'reports.links.globalStreamsByDsp.description',
    path: '/label/reports/global-streams-by-dsp',
  },
  {
    titleKey: 'reports.links.globalStreamsByVintage.title',
    descriptionKey: 'reports.links.globalStreamsByVintage.description',
    path: '/label/reports/global-streams-by-vintage',
  },
  {
    titleKey: 'reports.links.globalRevenueByVintage.title',
    descriptionKey: 'reports.links.globalRevenueByVintage.description',
    path: '/label/reports/global-revenue-by-vintage',
  },
];

function ReportLinkCard({ titleKey, descriptionKey, path }: ReportLink) {
  const { t } = useTranslation('label-perf');
  const title = t(titleKey);

  return (
    <Link to={path} className="no-underline">
      <div
        className={cn(
          BASE_LINK_ITEM_CLASSES,
          'h-full w-full max-w-md cursor-pointer flex-col p-lg',
        )}
        tabIndex={0}
        role="button"
        aria-label={t('reports.landing.openReport', { title })}
      >
        <div className="relative flex w-full flex-row items-center justify-between">
          <img
            src={ICON_BY_TYPE.WARNER_BLACK}
            className="size-2xl rounded-full transition-opacity duration-300 group-hover:opacity-none group-focus:opacity-none"
            alt="WMG icon"
          />
          <div
            aria-hidden
            className="absolute flex size-2xl items-center justify-center rounded-full opacity-none transition duration-300 bg-emphasis-subtle group-hover:opacity-full group-focus:opacity-full"
          >
            <Icon
              type="arrow-right"
              size="md"
              variant="regular"
              role="presentation"
              aria-hidden
              aria-label="navigate-icon"
              className="text-default"
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-sm">
          <p className="text-body-md leading-tight">{title}</p>
          <p className="text-body-xs leading-tight text-subtle">
            {t(descriptionKey)}
          </p>
          <div className="flex flex-row items-end justify-between gap-sm text-body-xs">
            <div className="min-w-min max-w-[11.5em] rounded-xs px-sm py-xs text-left leading-tight bg-emphasis-subtle">
              {t('header.title')}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

ReportLinkCard.displayName = 'ReportLinkCard';

export function ReportsLanding() {
  const { t } = useTranslation('label-perf');
  const title = t('reports.landing.title');

  return (
    <div className="flex w-full flex-1 flex-col gap-xl p-xl">
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className="flex w-full flex-col overflow-auto">
        <p className="py-lg text-headline-sm font-bold">{title}</p>
        <div
          className={cn(
            'grid gap-md overflow-auto overscroll-contain',
            'auto-cols-[277px] grid-cols-[repeat(auto-fit,277px)]',
            'auto-rows-[184px] grid-rows-[repeat(auto-fit,minmax(184px,1fr))]',
          )}
        >
          {REPORT_LINKS.map((link) => (
            <ReportLinkCard key={link.path} {...link} />
          ))}
        </div>
      </div>
    </div>
  );
}

ReportsLanding.displayName = 'ReportsLanding';
